import React from 'react';
import { Box, Paper, Typography, Stack, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const LegalLayout = ({ title, updatedAt, children }) => (
  <Box sx={{ maxWidth: 980, mx: 'auto' }}>
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={1.5}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">Last updated: {updatedAt}</Typography>
        <Typography variant="body2" color="text.secondary">
          These documents are provided for transparency and product operations. Review with legal counsel before launch.
        </Typography>
      </Stack>

      <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>{children}</Box>

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <MuiLink component={RouterLink} to="/legal/terms" underline="hover">Terms</MuiLink>
        <MuiLink component={RouterLink} to="/legal/privacy" underline="hover">Privacy</MuiLink>
        <MuiLink component={RouterLink} to="/legal/cookies" underline="hover">Cookie Policy</MuiLink>
      </Stack>
    </Paper>
  </Box>
);

export const TermsPage = () => (
  <LegalLayout title="Terms of Service" updatedAt="2026-06-14">
    <Typography>
      The service provides financial data, analytics, and tooling for informational and research purposes only.
      It does not provide investment advice, brokerage services, or fiduciary recommendations.
    </Typography>
    <Typography>
      You are responsible for independent decision-making and for verifying data before relying on it.
      Market data may be delayed, estimated, or sourced from third-party providers with independent terms.
    </Typography>
    <Typography>
      Access, subscription, and billing are managed through Stripe. Renewals, cancellations, and payment disputes
      are subject to your subscription plan and applicable payment processor terms.
    </Typography>
    <Typography>
      The service is provided "as is" without warranties of merchantability, fitness for a particular purpose,
      or guaranteed uptime/accuracy. Liability is limited to the maximum extent permitted by law.
    </Typography>
  </LegalLayout>
);

export const PrivacyPage = () => (
  <LegalLayout title="Privacy Policy" updatedAt="2026-06-14">
    <Typography>
      We collect account identity fields (for example Auth0 subject IDs and email), watchlists, notes, alerts,
      and billing entitlement metadata needed to operate the service.
    </Typography>
    <Typography>
      Payment card details are processed by Stripe and are not stored directly in this application database.
    </Typography>
    <Typography>
      We use logs and technical telemetry for security, abuse prevention, and reliability operations.
      Data retention periods should be defined by your compliance policy and jurisdictional requirements.
    </Typography>
    <Typography>
      If your jurisdiction provides access/erasure rights, contact support to submit a data request.
      You should implement automated export/deletion tooling before broad public launch.
    </Typography>
  </LegalLayout>
);

export const CookiePolicyPage = () => (
  <LegalLayout title="Cookie Policy" updatedAt="2026-06-14">
    <Typography>
      The application uses essential browser storage and authentication/session cookies required for login,
      security checks, and user experience features such as saved ticker selection and UI state.
    </Typography>
    <Typography>
      Third-party services (for example Auth0 or embedded media providers) may set additional cookies subject
      to their own policies.
    </Typography>
    <Typography>
      You can control cookies through your browser settings, but disabling essential cookies may prevent
      authentication and account features from functioning correctly.
    </Typography>
  </LegalLayout>
);
